import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs';
import { BnyRepository } from '@capital-access/bny/common';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import {
  getUserSettingsCulture,
  getUserSettingsCurrency,
  getUserSettingsLanguage,
  getUserSettingsTimezone
} from '@capital-access/common/globalization';
import { LocalizationService } from '@capital-access/common/localization';
import { getSelectedRelatedSecurities, getSelectedSecurity } from '@capital-access/common/security';
import { DEFAULT_TEMPLATE_KEY, SECTIONS_LAYOUT_KEY } from '@capital-access/common/utils';
import { FireflyModalService, NotificationService } from '@capital-access/firefly/components';
import { getDefaultSecurityListSettings } from '@capital-access/lists/common';
import { HostProvider } from '@capital-access/profiles/common';
import {
  BNY_MANAGEMENT_REPORT_ZOOM,
  EXPORT_OWNERSHIP_ANALYSIS_TEMPLATE_ID_KEY,
  getReportType,
  ITINERARY_REPORT_ZOOM,
  loadTemplateAction,
  OWNERSHIP_ANALYTICS_REPORT_ZOOM,
  removeLocalStorage,
  ReportDataService,
  ReportingTemplateRequest,
  ReportTemplate,
  ReportTemplateFormType,
  ReportTemplateNotifierService,
  ReportType,
  setZoomValue,
  TemplateLibraryService
} from '@capital-access/reporting/common';
import {
  convertReportTemplateToParsedSections,
  ReportTypeReportSections
} from '@capital-access/reporting/report-section';
import { REPORTING_TEMPLATE_PAGE, TEMPLATE_FORM_STORAGE_KEY } from '../constants';
import { DrawerService } from '../services/drawer.service';
import { NavigationIntentService } from '../services/navigation-intent.service';
import { TemplateService } from '../services/template.service';
import { createFormValueFromTemplate } from '../utils/forms';
import {
  createDuplicateTemplateSuccess,
  createTemplateFailure,
  createTemplateSuccess,
  deleteTemplateFailure,
  deleteTemplateSuccess,
  deletingTemplate,
  editTemplateFailure,
  editTemplateSuccess,
  loadAllTemplates,
  loadAllTemplatesFailure,
  loadAllTemplatesSuccess,
  openCreateReportingTemplateDrawer,
  openEditDuplicateReportingTemplateDrawer,
  resetCreateReportForm,
  saveCreateReportForm,
  setSelectedReportSectionsForPreview,
  setSelectedTemplateForPreview,
  submitReportForm,
  submitReportFormDuplicate,
  updateLandingPageReportData
} from './actions';
import {
  getCreateReportSectionSettings,
  getReportingTemplateRequest,
  getSelectedTemplateForPreview,
  getSelectedTemplateReportSections,
  getTemplateDrawerAction,
  selectTemplatesList
} from './selectors';
@Injectable()
export class SavedTemplateListEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly templateLibrarySvc: TemplateLibraryService,
    private drawerService: DrawerService,
    private modalSvc: FireflyModalService,
    private localization: LocalizationService,
    private store: Store,
    private templateService: TemplateService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private reportSections: ReportTypeReportSections,
    private reportDataService: ReportDataService,
    private reportTemplateNotifierService: ReportTemplateNotifierService,
    private navigationIntent: NavigationIntentService,
    private hostProvider: HostProvider,
    private bnyRepository: BnyRepository,
    @Inject('IS_CA_TEMPLATE') private readonly isCaTemplate: boolean
  ) {}
  loadTemplateList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllTemplates),
      switchMap(() =>
        forkJoin({
          templateList: this.templateLibrarySvc.getTemplates(),
          bnyAccount: this.bnyRepository.getIsInBnyAccount().pipe(catchError(() => of({ isBnyAccount: false })))
        }).pipe(
          map(({ templateList, bnyAccount }) => {
            const templates = templateList.filter(template => {
              if (!template) {
                return false;
              }
              if (!this.isCaTemplate) {
                // For Capital Access, if the user is not in a BNY account then don't return BNY Management Report templates -- BnyManagementReport should only show if the user is in a bny account
                if (!bnyAccount || !bnyAccount.isBnyAccount) {
                  return template.reportType !== ReportType.BnyManagementReport;
                }
                return true;
              } else {
                // In CA Admin only return templates with caTemplateInd true
                return template.caTemplateInd;
              }
            });
            return loadAllTemplatesSuccess({
              data: templates
            });
          }),
          catchError(error => of(loadAllTemplatesFailure({ error })))
        )
      )
    )
  );
  openCreateTemplateDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openCreateReportingTemplateDrawer),
        withLatestFrom(this.localization.getLocalization(REPORTING_TEMPLATE_PAGE)),
        tap(([_, localization]) => this.drawerService.openDrawer(localization, _.actionType, _.reportModule))
      ),
    { dispatch: false }
  );
  openEditTemplateDrawer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openEditDuplicateReportingTemplateDrawer),
        withLatestFrom(
          this.localization.getLocalization(REPORTING_TEMPLATE_PAGE),
          this.store.select(getTemplateDrawerAction),
          this.store.select(selectTemplatesList),
          this.store.select(getCreateReportSectionSettings)
        ),
        concatMap(([_, localization, actionType, templatesList, createReportSectionSettings]) => {
          let formDatas!: ReportingTemplateRequest;
          if (templatesList) {
            formDatas = createFormValueFromTemplate(
              _.template,
              actionType as ReportTemplateFormType,
              templatesList,
              createReportSectionSettings
            );
          }
          // Dispatch the form save action
          this.store.dispatch(
            submitReportForm({ formData: formDatas, actionType: actionType as ReportTemplateFormType })
          );
          // Wait for submitReportForm before proceeding
          return this.actions$.pipe(
            ofType(submitReportForm), // Ensure this action is dispatched after save completes
            take(1), // Take only the first occurrence
            tap(() => {
              this.drawerService.openDrawer(localization, actionType as ReportTemplateFormType); // Open the drawer
            })
          );
        })
      ),
    { dispatch: false } // No further actions dispatched by this effect
  );
  submitReportForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitReportForm),
      withLatestFrom(this.localization.getLocalization(REPORTING_TEMPLATE_PAGE)),
      filter(([action]) => action.actionType === ReportTemplateFormType.create),
      switchMap(([action, localization]) =>
        this.templateLibrarySvc.creatingTemplateRequest(action.formData).pipe(
          map(response => createTemplateSuccess({ templateId: response })), // Dispatch success action
          catchError(() => {
            this.notificationService.notify(localization['errorCreatingReport'], {
              type: 'danger'
            });
            return of(createTemplateFailure());
          }) // Dispatch failure action
        )
      )
    )
  );
  submitReportFormDuplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitReportFormDuplicate),
      withLatestFrom(this.localization.getLocalization(REPORTING_TEMPLATE_PAGE)),
      filter(([action]) => action.actionType === ReportTemplateFormType.create),
      switchMap(([action, localization]) =>
        this.templateLibrarySvc.creatingTemplateRequest(action.formData).pipe(
          map(response =>
            createDuplicateTemplateSuccess({ templateName: action.formData.templateName, templateId: response })
          ), // Dispatch success action
          catchError(() => {
            this.notificationService.notify(localization['errorCreatingReport'], {
              type: 'danger'
            });
            return of(createTemplateFailure());
          }) // Dispatch failure action
        )
      )
    )
  );
  submitReportFormSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createTemplateSuccess),
        withLatestFrom(this.store.select(getReportType)),
        switchMap(([action, reportType]) => {
          const url = this.router.serializeUrl(
            this.router.createUrlTree(['/reporting/template-designer'], {
              queryParams: { id: action.templateId, initial: 'true' }
            })
          );
          if (this.navigationIntent.consumeOpenInNewTab()) {
            const base = this.hostProvider.baseUrl;
            const absoluteUrl = base + url;
            window.open(absoluteUrl, '_blank');
            if (reportType) {
              this.store.dispatch(loadTemplateAction({ reportType }));
            }
          } else {
            this.router.navigate(['/reporting/template-designer'], {
              queryParams: { id: action.templateId, initial: 'true' }
            });
          }
          return of();
        })
      ),
    { dispatch: false } // This ensures the effect doesn't dispatch a new action
  );
  submitDuplicateReportFormSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createDuplicateTemplateSuccess),
        withLatestFrom(this.localization.getLocalization(REPORTING_TEMPLATE_PAGE)),
        tap(([action, localization]) => {
          this.store.dispatch(loadAllTemplates({ silentRefresh: true }));
          this.drawerService.closeDrawer();
          const actionMessage = ` ${action.templateName} ${
            localization['successfullyDuplicated'] || 'successfully duplicated'
          }`;
          this.notificationService.notify(actionMessage, {
            type: 'success'
          });
          this.router.navigate(['/reporting/template-designer'], {
            queryParams: { id: action.templateId }
          });
          if (this.route.snapshot.queryParams['id']) {
            this.reportTemplateNotifierService.notify({
              templateId: action.templateId
            } as Partial<ReportTemplate>);
          }
        })
      ),
    { dispatch: false }
  );
  resetCreateTemplateForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetCreateReportForm),
        tap(_ => {
          sessionStorage.removeItem(TEMPLATE_FORM_STORAGE_KEY);
        })
      ),
    { dispatch: false }
  );
  editTemplateSaving$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCreateReportForm),
      withLatestFrom(this.store.select(getSelectedTemplateForPreview)),
      switchMap(([action, template]) => {
        if (!template?.templateId) {
          return of(editTemplateFailure());
        }
        return this.templateService.editTemplate(template?.templateId, action.formData).pipe(
          map(response => editTemplateSuccess({ templateId: response })),
          catchError(() => of(editTemplateFailure()))
        );
      })
    )
  );
  editTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(editTemplateSuccess),
        withLatestFrom(
          this.store.select(getReportingTemplateRequest),
          this.localization.getLocalization(REPORTING_TEMPLATE_PAGE)
        ),
        tap(([action, reportTemplateFormData, localization]) => {
          this.store.dispatch(loadAllTemplates({ silentRefresh: true }));
          this.drawerService.closeDrawer();
          const reportName: ReportingTemplateRequest =
            reportTemplateFormData && typeof reportTemplateFormData !== 'string'
              ? reportTemplateFormData
              : ({ templateName: '', description: '', pageSize: '' } as ReportingTemplateRequest);
          const actionMessage = ` ${reportName?.templateName} ${localization['settingsUpdated']}`;
          this.notificationService.notify(actionMessage, {
            type: 'success'
          });
          if (this.route.snapshot.queryParams['id']) {
            this.reportTemplateNotifierService.notify({
              templateId: action.templateId
            } as Partial<ReportTemplate>);
          }
        })
      ),
    { dispatch: false } // No new actions dispatched here
  );
  editTemplateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(editTemplateFailure), // Listen for the success action
        withLatestFrom(
          this.store.select(getReportingTemplateRequest) // Selector for form values
        ),
        tap(([, reportTemplateFormData]) => {
          this.drawerService.closeDrawer();
          const reportName: ReportingTemplateRequest =
            reportTemplateFormData && typeof reportTemplateFormData !== 'string'
              ? reportTemplateFormData
              : ({ templateName: '', description: '', pageSize: '' } as ReportingTemplateRequest); // Provide a default object
          const actionMessage = ` ${reportName?.templateName} settings update failed`; // Create dynamic message
          this.notificationService.notify(actionMessage, {
            type: 'danger'
          });
        })
      ),
    { dispatch: false } // No new actions dispatched here
  );
  deletingTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deletingTemplate),
      withLatestFrom(
        this.localization.getLocalization(REPORTING_TEMPLATE_PAGE),
        this.store.select(selectTemplatesList)
      ),
      switchMap(([action, localization, list]) => {
        const selectedTemplate = list?.find(template => template.templateId === action.templateId);
        if (!selectedTemplate || selectedTemplate.templateId === undefined) {
          return of(deleteTemplateFailure({ template: selectedTemplate }));
        }
        const reportName = selectedTemplate.templateName || 'This report';
        return from(
          this.modalSvc.openConfirmation({
            style: 'error',
            title: localization['deleteReportTemplateTitle'],
            actionText: localization['delete'],
            dismissText: localization['cancel'],
            body: `${reportName} ${localization['templateDeletion']}`
          }).result
        ).pipe(
          concatMap(() =>
            this.templateService.deleteTemplate(action.templateId).pipe(
              map(() => deleteTemplateSuccess({ template: selectedTemplate })), // Success action
              catchError(() => of(deleteTemplateFailure({ template: selectedTemplate }))) // Failure action
            )
          )
        );
      })
    )
  );
  deletingTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteTemplateSuccess),
        switchMap(action => {
          const reportName = action?.template?.templateName;
          return this.localization.getLocalization(REPORTING_TEMPLATE_PAGE).pipe(
            take(1),
            tap(localization => {
              const actionMessage = `${reportName} ${localization['successfullyDeleted']}`;
              this.router.navigate(['/reporting']);
              this.notificationService.notify(actionMessage, {
                type: 'info'
              });
              //Delete default template and sections layout from local storage if the deleted template is set as default template
              //In this case One-pager will be the default report layout
              const defaultTemplateId = Number(
                localStorage.getItem(`${action?.template?.reportType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()) ?? 0
              );
              if (defaultTemplateId == action?.template?.templateId) {
                const keysToRemove =
                  action?.template?.reportType === ReportType.OwnershipAnalyticsReport
                    ? [`${action?.template?.reportType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()]
                    : [
                        `${action?.template?.reportType}_${SECTIONS_LAYOUT_KEY}`.toUpperCase(),
                        `${action?.template?.reportType}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase()
                      ];
                this.store.dispatch(
                  removeLocalStorage({
                    keys: keysToRemove
                  })
                );
              }
              //Need to reset the selected template id in localStorage if the deleted template is set as default template
              if (
                action?.template?.reportType === ReportType.OwnershipAnalyticsReport &&
                Number(localStorage.getItem(EXPORT_OWNERSHIP_ANALYSIS_TEMPLATE_ID_KEY) ?? 0) ===
                  action?.template?.templateId
              ) {
                this.store.dispatch(removeLocalStorage({ keys: [EXPORT_OWNERSHIP_ANALYSIS_TEMPLATE_ID_KEY] }));
              }
              this.store.dispatch(loadAllTemplates({ silentRefresh: true }));
            })
          );
        })
      ),
    { dispatch: false }
  );
  deletingTemplateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteTemplateFailure),
        switchMap(action => {
          const reportName = action.template?.templateName;
          return this.localization.getLocalization(REPORTING_TEMPLATE_PAGE).pipe(
            take(1),
            tap(localization => {
              const actionMessage = `${reportName} ${localization['failedToDelete']}`;
              this.notificationService.notify(actionMessage, {
                type: 'danger'
              });
            })
          );
        })
      ),
    { dispatch: false }
  );
  getTemplateDetailsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedTemplateForPreview),
      filter(action => !!action.selectedTemplate),
      switchMap(action => this.templateService.getTemplateDetailsById(action.selectedTemplate.templateId)),
      switchMap(template => {
        const { sections, pageSize } = convertReportTemplateToParsedSections(template, this.reportSections);
        return [
          setSelectedReportSectionsForPreview({
            createReportSectionSettings: template.settings,
            reportSections: sections,
            templatePageSize: pageSize
          })
        ];
      })
    )
  );

  private lastTemplateAction: ReturnType<typeof setSelectedTemplateForPreview> | null = null;
  private previousReportType: ReportType | null = null;
  private previousTemplateId: number | null | undefined = null;
  private initialized = false;

  trackTemplateSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSelectedTemplateForPreview),
        tap(action => {
          this.lastTemplateAction = action;
        })
      ),
    { dispatch: false }
  );

  initializeZoomForReportType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedReportSectionsForPreview),
      withLatestFrom(this.store.select(getSelectedTemplateForPreview)),
      switchMap(
        ([_action, selectedTemplate]: [
          ReturnType<typeof setSelectedReportSectionsForPreview>,
          ReportTemplate | null | undefined
        ]) => {
          const reportType = selectedTemplate?.reportType as ReportType | null;

          // Only skip zoom reset if navigating to the designer for editing (skipZoomReset: true)
          // and the template ID hasn't changed (we're staying within the same template)
          const shouldSkipZoomReset =
            this.lastTemplateAction?.skipZoomReset && selectedTemplate?.templateId === this.previousTemplateId;

          this.lastTemplateAction = null;

          if (shouldSkipZoomReset) {
            this.previousReportType = reportType;
            this.previousTemplateId = selectedTemplate?.templateId ?? null;
            return EMPTY;
          }

          let requiredZoom: number;
          if (reportType === ReportType.OwnershipAnalyticsReport) {
            requiredZoom = OWNERSHIP_ANALYTICS_REPORT_ZOOM;
          } else if (reportType === ReportType.BnyManagementReport || reportType === ReportType.PeerOwnershipReport) {
            requiredZoom = BNY_MANAGEMENT_REPORT_ZOOM;
          } else if (reportType === ReportType.Itinerary) {
            requiredZoom = ITINERARY_REPORT_ZOOM;
          } else {
            requiredZoom = 1;
          }
          const reportTypeChanged = reportType !== this.previousReportType;
          const templateIdChanged = selectedTemplate?.templateId !== this.previousTemplateId;
          const isFirstLoad = !this.initialized;
          this.previousReportType = reportType;
          this.previousTemplateId = selectedTemplate?.templateId ?? null;
          this.initialized = true;
          if (isFirstLoad || reportTypeChanged || templateIdChanged) {
            return of(setZoomValue({ zoom: requiredZoom }));
          }
          return EMPTY;
        }
      )
    )
  );
  loadSampleReportData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedReportSectionsForPreview),
      withLatestFrom(
        this.store.select(getSelectedTemplateReportSections),
        this.isCaTemplate ? of(null) : this.store.select(getSelectedSecurity),
        this.store.select(getUserSettingsCulture),
        this.store.select(getUserSettingsLanguage),
        this.store.select(getUserSettingsCurrency),
        this.store.select(getUserSettingsTimezone),
        this.store.select(getDefaultSecurityListSettings),
        this.isCaTemplate ? of(null) : this.store.select(getSelectedRelatedSecurities),
        this.store.select(getFeatureToggle('engage-activities-participant-biography'))
      ),
      switchMap(
        ([
          ,
          reportSections,
          security,
          culture,
          language,
          currency,
          timezone,
          defaultPeerList,
          relatedSecurities,
          engageActivitiesParticipantBiographyEnabled
        ]) =>
          this.reportDataService.getReportData(
            reportSections,
            security,
            culture,
            language,
            currency,
            timezone,
            defaultPeerList.id!,
            relatedSecurities,
            engageActivitiesParticipantBiographyEnabled
          )
      ),
      map(reportData => updateLandingPageReportData({ reportData }))
    )
  );
}
